import request from "@/libs/request";
import {
  AuditLogsProjectGroupsItemSheetRequest,
  AuditLogsProjectGroupsItemSheetResponse,
  ChangeToNonSmfProductProjectGroupsItemsheetRequest,
  ChangeToSmfProductProjectGroupsItemsheetRequest,
  CreateProjectGroupsItemSheetFinalizedRequest,
  CreateProjectGroupsItemSheetFinalizedResponse,
  CreateProjectGroupsItemSheetFromFileRequest,
  CreateProjectGroupsItemSheetFromFileResponse,
  CreateProjectGroupsItemSheetFromOriginalRequest,
  CreateProjectGroupsItemSheetFromOriginalResponse,
  CreateProjectGroupsItemSheetRequest,
  CreateProjectGroupsItemSheetResponse,
  DeleteProjectGroupsItemSheetFinalizedRequest,
  DeleteProjectGroupsItemSheetFinalizedResponse,
  DeleteProjectGroupsItemSheetLatestRequest,
  DeleteProjectGroupsItemSheetLatestResponse,
  DownloadProjectGroupsItemSheetRequest,
  DownloadProjectGroupsItemSheetResponse,
  DuplicateProjectGroupsItemSheetLatestRequest,
  DuplicateProjectGroupsItemSheetLatestResponse,
  OverwriteProjectGroupsItemSheetFromFileRequest,
  OverwriteProjectGroupsItemSheetFromFileResponse,
  ReadProjectGroupsItemSheetFinalizedRequest,
  ReadProjectGroupsItemSheetFinalizedResponse,
  ReadProjectGroupsItemSheetLatestRequest,
  ReadProjectGroupsItemSheetLatestResponse,
  ReadProjectGroupsItemSheetOriginalInfoRequest,
  ReadProjectGroupsItemSheetOriginalInfoResponse,
  ReadProjectGroupsItemSheetOriginalListResponse,
  ReadItemsheetInProgressCountResponse,
  ReadProjectGroupsItemSheetRequest,
  ReadProjectGroupsItemSheetResponse,
  RestartItemsheetOriginalRequest,
  SearchSmfRequest,
  SearchSmfResponse,
  UpdateProjectGroupsItemSheetLatestRequest,
  UpdateProjectGroupsItemSheetLatestResponse,
  HighlightProjectGroupsItemsheetRequest,
} from "@/types/itemsheet";

// 산출내역서 확정 생성
export const createProjectGroupsItemSheetFinalized = async ({
  group_id,
  data,
}: {
  group_id: string;
  data: CreateProjectGroupsItemSheetFinalizedRequest;
}): Promise<CreateProjectGroupsItemSheetFinalizedResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/create-finalized`,
    data
  );
  return response.data;
};

// 산출내역서 확정 조회
export const readProjectGroupsItemSheetFinalized = async ({
  group_id,
  data,
}: {
  group_id: string;
  data: ReadProjectGroupsItemSheetFinalizedRequest;
}): Promise<ReadProjectGroupsItemSheetFinalizedResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/read-finalized`,
    data
  );
  return response.data;
};

// 산출내역서 확정 삭제
export const deleteProjectGroupsItemSheetFinalized = async ({
  group_id,
  data,
}: {
  group_id: string;
  data: DeleteProjectGroupsItemSheetFinalizedRequest;
}): Promise<DeleteProjectGroupsItemSheetFinalizedResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/delete-finalized`,
    data
  );
  return response.data;
};

// 산출내역서 원본 정보 조회 (단일)
export const readProjectGroupsItemSheetOriginalInfo = async ({
  group_id,
  data,
}: {
  group_id: string;
  data: ReadProjectGroupsItemSheetOriginalInfoRequest;
}): Promise<ReadProjectGroupsItemSheetOriginalInfoResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/read-original-info`,
    data
  );
  return response.data;
};

// 산출내역서 원본 목록 조회
export const readProjectGroupsItemSheetOriginalList = async ({
  group_id,
}: {
  group_id: string;
}): Promise<ReadProjectGroupsItemSheetOriginalListResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/read-original-list`
  );
  return response.data;
};

// 산출내역서 생성 진행 중 개수 조회
export const readItemsheetInProgressCount = async ({
  group_id,
}: {
  group_id: string;
}): Promise<ReadItemsheetInProgressCountResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/in-progress-count`
  );
  return response.data;
};

// 산출내역서 생성 재시작
export const restartItemsheetOriginal = async ({
  group_id,
  data,
}: {
  group_id: string;
  data: RestartItemsheetOriginalRequest;
}): Promise<void> => {
  await request.post(
    `/project-groups/${group_id}/itemsheets/restart-original`,
    data
  );
};

// 산출내역서 최신본(latest) 생성 API (파일 업로드).
export const createProjectGroupsItemSheetFromFile = async ({
  group_id,
  data,
}: {
  group_id: string;
  data: CreateProjectGroupsItemSheetFromFileRequest;
}): Promise<CreateProjectGroupsItemSheetFromFileResponse> => {
  const { project_id, project_type, original_id, uploaded_file } = data;

  const formData = new FormData();
  formData.append("project_id", project_id);
  formData.append("project_type", project_type);
  formData.append("original_id", original_id);
  formData.append("uploaded_file", uploaded_file);

  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/create-from-file`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

// 산출내역서 최신본(latest) 덮어쓰기 API (파일 업로드).
export const overwriteProjectGroupsItemSheetFromFile = async ({
  group_id,
  latest_sheet_id,
  data,
}: {
  group_id: string;
  latest_sheet_id: string;
  data: OverwriteProjectGroupsItemSheetFromFileRequest;
}): Promise<OverwriteProjectGroupsItemSheetFromFileResponse> => {
  const { project_id, project_type, original_id, uploaded_file } = data;

  const formData = new FormData();
  formData.append("project_id", project_id);
  formData.append("project_type", project_type);
  formData.append("original_id", original_id);
  formData.append("uploaded_file", uploaded_file);

  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/${latest_sheet_id}/overwrite-from-file`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

// 산출내역서 최신본(lastest) 생성 API (원본 복사)
export const createProjectGroupsItemSheetFromOriginal = async ({
  group_id,
  data,
}: {
  group_id: string;
  data: CreateProjectGroupsItemSheetFromOriginalRequest;
}): Promise<CreateProjectGroupsItemSheetFromOriginalResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/create-from-original`,
    data
  );
  return response.data;
};

// 산출내역서 원본 및 최신본(latest) 생성
export const createProjectGroupsItemSheets = async ({
  group_id,
  data,
}: {
  group_id: string;
  data: CreateProjectGroupsItemSheetRequest;
}): Promise<CreateProjectGroupsItemSheetResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/create`,
    data
  );
  return response.data;
};

// 산출내역서 최신 버전 목록 조회
export const readProjectGroupsItemSheet = async ({
  group_id,
  data,
}: {
  group_id: string;
  data: ReadProjectGroupsItemSheetRequest;
}): Promise<ReadProjectGroupsItemSheetResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/read`,
    data
  );
  return response.data;
};

// 산출내역서 최신 버전 조회
export const readProjectGroupsItemSheetLatest = async ({
  group_id,
  latest_sheet_id,
  data,
}: {
  group_id: string;
  latest_sheet_id: string;
  data: ReadProjectGroupsItemSheetLatestRequest;
}): Promise<ReadProjectGroupsItemSheetLatestResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/${latest_sheet_id}/read`,
    data
  );
  return response.data;
};

// 산출내역서 최신 버전 수정
export const updateProjectGroupsItemSheetLatest = async ({
  group_id,
  latest_sheet_id,
  data,
}: {
  group_id: string;
  latest_sheet_id: string;
  data: UpdateProjectGroupsItemSheetLatestRequest;
}): Promise<UpdateProjectGroupsItemSheetLatestResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/${latest_sheet_id}/update`,
    data
  );
  return response.data;
};

// 산출내역서 최신 버전 삭제
export const deleteProjectGroupsItemSheetLatest = async ({
  group_id,
  latest_sheet_id,
  data,
}: {
  group_id: string;
  latest_sheet_id: string;
  data: DeleteProjectGroupsItemSheetLatestRequest;
}): Promise<DeleteProjectGroupsItemSheetLatestResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/${latest_sheet_id}/delete`,
    data
  );
  return response.data;
};

// 산출내역서 최신 버전 복사
export const duplicateProjectGroupsItemSheetLatest = async ({
  group_id,
  latest_sheet_id,
  data,
}: {
  group_id: string;
  latest_sheet_id: string;
  data: DuplicateProjectGroupsItemSheetLatestRequest;
}): Promise<DuplicateProjectGroupsItemSheetLatestResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/${latest_sheet_id}/duplicate`,
    data
  );
  return response.data;
};

// 산출내역서 엑셀 파일 다운로드
export const downloadProjectGroupsItemSheet = async ({
  group_id,
  latest_sheet_id,
  data,
}: {
  group_id: string;
  latest_sheet_id: string;
  data: DownloadProjectGroupsItemSheetRequest;
}): Promise<DownloadProjectGroupsItemSheetResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/${latest_sheet_id}/download`,
    data,
    {
      responseType: "blob",
    }
  );
  return { file: response.data };
};

// 산출내역서 감사 로그 조회
export const auditLogsProjectGroupsItemSheet = async ({
  group_id,
  data,
}: {
  group_id: string;
  data: AuditLogsProjectGroupsItemSheetRequest;
}): Promise<AuditLogsProjectGroupsItemSheetResponse> => {
  const response = await request.post(
    `/project-groups/${group_id}/itemsheets/auditlogs`,
    data
  );
  return response.data;
};

// 산출내역서 자사 제품으로 제품ID 변경
export const changeToSmfProductId = async ({
  group_id,
  latest_sheet_id,
  data,
}: {
  group_id: string;
  latest_sheet_id: string;
  data: ChangeToSmfProductProjectGroupsItemsheetRequest;
}): Promise<void> => {
  await request.post(
    `/project-groups/${group_id}/itemsheets/${latest_sheet_id}/change-to-smf-product`,
    data
  );
};

// 산출내역서 타사 제품으로 제품ID 변경
export const changeToNonSmfProductId = async ({
  group_id,
  latest_sheet_id,
  data,
}: {
  group_id: string;
  latest_sheet_id: string;
  data: ChangeToNonSmfProductProjectGroupsItemsheetRequest;
}): Promise<void> => {
  await request.post(
    `/project-groups/${group_id}/itemsheets/${latest_sheet_id}/change-to-tpp-product`,
    data
  );
};

// 산출내역서 하이라이트 적용
export const highlightProjectGroupsItemsheet = async ({
  group_id,
  latest_sheet_id,
  data,
}: {
  group_id: string;
  latest_sheet_id: string;
  data: HighlightProjectGroupsItemsheetRequest;
}): Promise<void> => {
  await request.post(
    `/project-groups/${group_id}/itemsheets/${latest_sheet_id}/highlight`,
    data
  );
};

// 자사제품(SMF) 검색
export const searchSMF = async (
  data: SearchSmfRequest
): Promise<SearchSmfResponse> => {
  const response = await request.post(`/smf/search`, data);
  return response.data;
};
